import { StandardEvents } from '@shopify/events';

const SECTION_ID = 'cart-suggestions';

/**
 * Loads the cart's suggestions row from the product recommendations route.
 *
 * The row is not on any template, so it cannot be rendered with the page. It is
 * fetched from the recommendations route with `section_id`, which is also what
 * makes Shopify's `related` model the source without an app or a collection to
 * maintain.
 *
 * It reloads on every cart change rather than caching, which is the opposite of
 * Horizon's own product-recommendations component. That one caches per URL and
 * latches after one load, both correct on a product page where nothing moves.
 * Here the anchor product and the amount still needed both change as the bag
 * does, and neither is in the URL, so a cached response would show the previous
 * bag's heading.
 *
 * The element carries data-skip-node-update, so the cart's own re-render leaves
 * this subtree alone. Without it the row raced its own container: the fetch
 * landed, then the cart morphed the empty placeholder back over the top, and
 * the row read as broken while the server had answered correctly all along.
 * That makes the component the only thing that writes here, so it reads the
 * anchor from the cart at fetch time rather than from an attribute the
 * re-render is no longer allowed to refresh.
 */
class CartSuggestions extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * The cart's own re-render empties this row's container without replacing the
   * element, so there is no reconnect to hang a reload on. Rather than guess
   * which event fires and in what order, the row watches its own container and
   * refills whenever something empties it. It self-heals no matter what did it.
   * @type {MutationObserver}
   */
  #emptied = new MutationObserver(() => {
    if (this.#activeFetch) return;
    const target = this.querySelector(`#shopify-section-${SECTION_ID}`);
    if (target && !target.childNodes.length) this.#load();
  });

  connectedCallback() {
    this.#load();
    this.#emptied.observe(this, { childList: true, subtree: true });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    this.#emptied.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#activeFetch?.abort();
  }

  #onCartUpdate = () => this.#load();

  async #load() {
    const { url } = this.dataset;
    if (!url) return;

    this.#activeFetch?.abort();
    // Kept locally so this load only ever clears its own controller. Clearing
    // this.#activeFetch unconditionally let an aborted load, finishing late,
    // wipe the NEXT load's controller -- after which nothing aborted that one,
    // and a stale response could land last and win.
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const { signal } = controller;
      // no-store on both: the anchor and the amount still needed change with
      // the bag but neither is in the URL, so a cached answer is the previous
      // bag's (the same trap assets/free-shipping-line.js hit with /cart.js).
      const cart = await (await fetch('/cart.js', { signal, cache: 'no-store' })).json();

      // The last line is the piece just added, and so the freshest signal of
      // what this shopper is actually shopping for.
      const anchor = cart.items?.at(-1)?.product_id;
      if (!anchor) return this.#clear();

      const endpoint = `${url}?product_id=${anchor}&limit=10&intent=related&section_id=${SECTION_ID}`;
      const response = await fetch(endpoint, { signal, cache: 'no-store' });
      if (!response.ok) return this.#clear();

      const html = await response.text();
      // An empty body is the section deciding it has nothing worth showing --
      // every suggestion already in the bag, or sold out. Not an error.
      if (!html.trim().length) return this.#clear();

      // Deliberately not morphSection. That helper resolves the section
      // against the ones the page was rendered with, and this row is on no
      // template, so it returns silently having done nothing -- no error to
      // catch, just an empty row. The markup is replaced directly instead, and
      // the custom elements inside it upgrade on insertion.
      const target = this.querySelector(`#shopify-section-${SECTION_ID}`);
      if (!target) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = parsed.getElementById(`shopify-section-${SECTION_ID}`);
      target.replaceChildren(...(incoming ? incoming.childNodes : []));
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      this.#clear();
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  #clear() {
    const target = this.querySelector(`#shopify-section-${SECTION_ID}`);
    if (target) target.innerHTML = '';
  }
}

if (!customElements.get('cart-suggestions')) {
  customElements.define('cart-suggestions', CartSuggestions);
}
